<script setup>
import { computed } from 'vue';
import CourseModalScheduleList from '@/js/core/components/pages/course-modal/CourseModalScheduleList.vue';
import PersonNameLink from '@/js/core/components/parts/PersonNameLink.vue';

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
  activeProfileUuid: {
    type: String,
    default: null,
  },
  personPanelId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['person-click']);

const facultyList = computed(() =>
  Array.isArray(props.course.faculty)
    ? props.course.faculty.filter(faculty => faculty.displayName)
    : []
);

const handlePersonClick = (uuid, buttonEl) => {
  emit('person-click', uuid, buttonEl);
};

const unitsLabel = computed(() => {
  if (props.course.units == null) return '';
  return `${props.course.units} ${props.course.units === 1 ? 'unit' : 'units'}`;
});

const hasTopBox = computed(
  () => props.course.department || props.course.number || facultyList.value.length > 0
);
const hasScheduleBox = computed(
  () => Array.isArray(props.course.sessions) && props.course.sessions.length > 0
);
const hasLocationBox = computed(() => !!props.course.location);
const hasTermBox = computed(() => !!props.course.type || !!unitsLabel.value);
</script>

<template>
  <div class="course-modal__meta-info-container">
    <ul class="course-modal__meta-info">
      <li v-if="hasTopBox" class="course-modal__meta-info-row">
        <template v-if="course.department">
          <span class="course-modal__info-row-heading">Department</span>
          <span class="course-modal__info-row-content">{{ course.department }}</span>
        </template>
        <template v-if="course.number">
          <span class="course-modal__info-row-heading">Course Number</span>
          <span class="course-modal__info-row-content">{{ course.number }}</span>
        </template>
        <template v-if="facultyList.length">
          <span class="course-modal__info-row-heading">Faculty</span>
          <div class="course-modal__info-row-content">
            <ul class="course-modal__faculty-list">
              <li
                v-for="faculty in facultyList"
                :key="faculty.uuid || faculty.displayName"
                class="course-modal__faculty-item"
              >
                <PersonNameLink
                  :name="faculty.displayName"
                  :profile-uuid="faculty.uuid || null"
                  :expanded="!!activeProfileUuid && activeProfileUuid === faculty.uuid"
                  :controls="personPanelId"
                  class="course-modal__faculty-link"
                  @click="handlePersonClick"
                />
              </li>
            </ul>
          </div>
        </template>
      </li>

      <li v-if="hasScheduleBox" class="course-modal__meta-info-row">
        <span class="course-modal__info-row-heading">
          Schedule<template v-if="course.scheduleTimezoneLabel">
            (All times are in {{ course.scheduleTimezoneLabel }})</template
          >
        </span>
        <div class="course-modal__info-row-content">
          <CourseModalScheduleList :sessions="course.sessions" />
        </div>
      </li>

      <li v-if="hasLocationBox" class="course-modal__meta-info-row">
        <span class="course-modal__info-row-heading">Location</span>
        <span class="course-modal__info-row-content">{{ course.location }}</span>
      </li>

      <li v-if="hasTermBox" class="course-modal__meta-info-row last-box">
        <span class="course-modal__info-row-heading">{{ course.termLabel }}</span>
        <span v-if="course.type" class="course-modal__info-row-content">{{ course.type }}</span>
        <span v-if="unitsLabel" class="course-modal__info-row-content">{{ unitsLabel }}</span>
      </li>
    </ul>
  </div>
</template>

<style scoped></style>
